import React, { useEffect, useRef } from "react";
import type { JSX } from "react";

type Ball = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  gradStops: [string, string];
  frozen: boolean;
  highlightOffset: { x: number; y: number };
};

export default function FloatingBalls(): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const homeEl = document.querySelector<HTMLElement>(".home-sec");
    const innEl = document.querySelector<HTMLElement>(".inn-sec");

    const docTop = (homeEl && homeEl.getBoundingClientRect().top + window.scrollY) ?? 0;
    const homeHeight = homeEl?.getBoundingClientRect().height ?? window.innerHeight;
    const innHeight = innEl?.getBoundingClientRect().height ?? window.innerHeight;
    const coverHeight = Math.max(homeHeight + innHeight, window.innerHeight);

    canvas.style.position = "absolute";
    canvas.style.left = "0px";
    canvas.style.top = `${docTop}px`;
    canvas.style.height = `${coverHeight}px`;
    canvas.width = Math.round(window.innerWidth * (window.devicePixelRatio || 1));
    canvas.height = Math.round(coverHeight * (window.devicePixelRatio || 1));
    ctx.setTransform(window.devicePixelRatio || 1, 0, 0, window.devicePixelRatio || 1, 0, 0);

    //  const gradients: [string, string][] = [
    //   ["#DA4399", "#FE97D1"],
    //   ["#AA0909", "#F65151"],
    //   [ "#7B27C5", "#AA67E5"],
    //   ["#43AE9E", "#A7EFE4"],
    //   ["#2C47B2", "#A9B9F9"],
    //   ["#D7AF1F", "#FAD85D"],
    //   ["#20B74D", "#B8F3CB"],
    //   ["#D6561E", "#FEDC65"],
    // ];

    //  const gradients: [string, string][] = [
    //   ["#DA4399", "#DA4399"],
    //   ["#AA0909", "#AA0909"],
    //   ["#7B27C5", "#7B27C5"],
    //   ["#43AE9E", "#43AE9E"],
    //   ["#2C47B2", "#2C47B2"],
    //   ["#D7AF1F", "#D7AF1F"],
    //   ["#20B74D", "#20B74D"],
    //   ["#D6561E", "#D6561E"],
    // ];

      const gradients: [string, string][] = [
      ["#DA4399", "#E35BA7"],
      ["#AA0909", "#C52A2A"],
      ["#7B27C5", "#9250D4"],
      ["#43AE9E", "#5BC1B2"],
      ["#2C47B2", "#4B63C8"],
      ["#D7AF1F", "#E3C24A"],
      ["#20B74D", "#3ECC6B"],
      ["#D6561E", "#E1733F"],
    ];

  

    const baseSize = Math.min(window.innerWidth, window.innerHeight);
    const minR = Math.round(baseSize * 0.22);
    const maxR = Math.round(baseSize * 0.38);

    const balls: Ball[] = gradients.map((g, i) => {
      const r = Math.random() * (maxR - minR) + minR;
      const x = Math.random() * (window.innerWidth - r * 5) + r;
      const y = Math.random() * (coverHeight - r * 5) + r;
      
      const baseSpeed = 10;
      const vx = (Math.random() - 0.5) * baseSpeed * (i % 2 ? 5.2 : 0.9);
      const vy = (Math.random() - 0.5) * baseSpeed * (i % 3 ? 1.1 : 0.9);
      
      const highlightOffset = {
        x: (Math.random() - 0.5) * r * 0.5,
        y: (Math.random() - 0.5) * r * 0.5,
      };
      return { x, y, vx, vy, r, gradStops: g, frozen: false, highlightOffset };
    });

    const separate = () => {
      let any = false;
      for (let a = 0; a < balls.length; a++) {
        for (let b = a + 1; b < balls.length; b++) {
          const A = balls[a],
            B = balls[b];
          const dx = B.x - A.x;
          const dy = B.y - A.y;
          const dist = Math.hypot(dx, dy) || 1;
          const minDist = A.r + B.r + 4;
          if (dist < minDist) {
            const overlap = (minDist - dist) / 2;
            const nx = dx / dist;
            const ny = dy / dist;
            A.x -= nx * overlap;
            A.y -= ny * overlap;
            B.x += nx * overlap;
            B.y += ny * overlap;
            any = true;
          }
        }
      }
      return any;
    };
    for (let i = 0; i < 8; i++) {
      if (!separate()) break;
    }

    const detect = (A: Ball, B: Ball) => {
      const dx = B.x - A.x;
      const dy = B.y - A.y;
      const d = Math.hypot(dx, dy) || 1;
      return { collided: d < A.r + B.r, dx, dy, d };
    };

    const resolve = (A: Ball, B: Ball, info: { dx: number; dy: number; d: number }) => {
      const overlap = A.r + B.r - info.d;
      const nx = info.dx / info.d;
      const ny = info.dy / info.d;
      A.x -= nx * (overlap / 2);
      A.y -= ny * (overlap / 2);
      B.x += nx * (overlap / 2);
      B.y += ny * (overlap / 2);
      const ax = A.vx,
        ay = A.vy;
      A.vx = B.vx * 0.95;
      A.vy = B.vy * 0.95;
      B.vx = ax * 0.95;
      B.vy = ay * 0.95;
    };

    const mouse = { x: null as number | null, y: null as number | null };
    const onMouse = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY - docTop;
    };
    window.addEventListener("mousemove", onMouse);

    const step = () => {
      ctx.clearRect(
        0,
        0,
        canvas.width / (window.devicePixelRatio || 1),
        canvas.height / (window.devicePixelRatio || 1)
      );

      for (const b of balls) {
        if (!b.frozen) {
          b.vx += (Math.random() - 0.5) * 0.030;
          b.vy += (Math.random() - 0.5) * 0.030;
          const maxSpeed = 2.5;
          b.vx = Math.max(-maxSpeed, Math.min(maxSpeed, b.vx));
          b.vy = Math.max(-maxSpeed, Math.min(maxSpeed, b.vy));
        }
      }

      for (let i = 0; i < balls.length; i++) {
        const b = balls[i];
        if (mouse.x !== null && mouse.y !== null) {
          b.frozen = Math.hypot(mouse.x - b.x, mouse.y - b.y) < b.r;
        }

        if (!b.frozen) {
          b.x += b.vx;
          b.y += b.vy;
        }

        const cw = parseFloat(canvas.style.width || `${window.innerWidth}`);
        const ch = parseFloat(canvas.style.height || `${coverHeight}`);
        if (b.x - b.r < 0) {
          b.x = b.r;
          b.vx *= -1;
        }
        if (b.x + b.r > cw) {
          b.x = cw - b.r;
          b.vx *= -1;
        }
        if (b.y - b.r < 0) {
          b.y = b.r;
          b.vy *= -1;
        }
        if (b.y + b.r > ch) {
          b.y = ch - b.r;
          b.vy *= -1;
        }

        for (let j = i + 1; j < balls.length; j++) {
          const other = balls[j];
          const info = detect(b, other);
          if (info.collided) resolve(b, other, info);
        }

    const gx = b.x + b.highlightOffset.x;
        const gy = b.y + b.highlightOffset.y;

        const grad = ctx.createRadialGradient(
          gx,
          gy,
          b.r * 0.05, 
          b.x,
          b.y,
          b.r
        );

        grad.addColorStop(0, b.gradStops[1] + "EE");   
        grad.addColorStop(0.35, b.gradStops[1] + "CC");
        grad.addColorStop(0.7, b.gradStops[0] + "CC");
        grad.addColorStop(1, b.gradStops[0] + "EE");  


        ctx.beginPath();
        ctx.fillStyle = grad;
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(step);
    };

    animRef.current = requestAnimationFrame(step);

    const onResize = () => {
      const newW = window.innerWidth;
      const newTop = (homeEl && homeEl.getBoundingClientRect().top + window.scrollY) ?? 0;
      const newHomeH = homeEl?.getBoundingClientRect().height ?? window.innerHeight;
      const newInnH = innEl?.getBoundingClientRect().height ?? window.innerHeight;
      const newCoverH = Math.max(newHomeH + newInnH, window.innerHeight);

      const prevW = parseFloat(canvas.style.width || `${window.innerWidth}`);
      const prevH = parseFloat(canvas.style.height || `${coverHeight}`);
      const sx = newW / (prevW || newW);
      const sy = newCoverH / (prevH || newCoverH);
      balls.forEach((b) => {
        b.x *= sx;
        b.y *= sy;
      });

      canvas.style.top = `${canvas.parentElement?.clientTop}px`;
      canvas.style.width = `${canvas.parentElement?.clientWidth}px`;
      canvas.style.height = `${canvas.parentElement?.clientHeight}px`;
      canvas.width = Math.round(newW * (window.devicePixelRatio || 1));
      ctx.setTransform(window.devicePixelRatio || 1, 0, 0, window.devicePixelRatio || 1, 0, 0);
    };
    window.addEventListener("resize", onResize);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      window.removeEventListener("mousemove", onMouse);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return <canvas ref={canvasRef} className="floating-balls-canvas" />;
}
